import { z } from 'zod';
/**
 * The workforce reference data an agency starts with (§6.3.5, §17.3).
 *
 * ## Data, not enums
 *
 * §6.3.5 lists eleven credential types and §17.3 lists five kinds of onboarding
 * step. Both are **starting points**, and both are seeded as ordinary
 * tenant-scoped rows an agency may rename, disable, or add to.
 *
 * The temptation is an enum, and it is wrong here for a reason that has already
 * bitten this product once with allergens: an agency operating in a second state
 * meets a registry check the first state does not have, and a closed enum turns
 * that into a migration. Worse, it turns it into a migration that changes a type
 * every other agency's rows are stored against.
 *
 * The list below is therefore what an agency is *given*, not what an agency is
 * *limited to*. `key` is stable so the seed is re-runnable and so code can find
 * a well-known type; `name` is what the agency renames it to and is never read
 * by code.
 *
 * ## `blocksScheduling` is the load-bearing field
 *
 * It decides whether a lapse refuses a shift or merely warns. Marking everything
 * blocking would be the safe-looking choice and is the wrong one — an agency
 * whose caregivers are all blocked by a missing in-service hour stops using the
 * compliance feature, and then nothing blocks anything.
 *
 * The split below follows what actually makes care unlawful or unbillable:
 * certifications, background and registry checks, TB screening and the I-9 block.
 * A physical exam, in-service hours, an immunisation attestation and — unless
 * the caregiver drives — a licence and insurance warn.
 */
export const credentialCategorySchema = z.enum([
    'LICENSE',
    'CERTIFICATION',
    'HEALTH_SCREENING',
    'BACKGROUND_CHECK',
    'TRAINING',
    'DOCUMENT',
]);
/** §6.3.5's list, with the blocking decision made explicitly for each. */
export const SEEDED_CREDENTIAL_TYPES = [
    {
        key: 'AIDE_CERTIFICATION',
        name: 'CNA / HHA / PCA certification',
        category: 'CERTIFICATION',
        requiredForDisciplines: ['PCA', 'HHA'],
        blocksScheduling: true,
        renewalPeriodMonths: 24,
        rationale: 'Delivering aide services without it is unlicensed practice, and unbillable.',
    },
    {
        key: 'CPR_FIRST_AID',
        name: 'CPR / First Aid',
        category: 'CERTIFICATION',
        requiredForDisciplines: [],
        blocksScheduling: true,
        renewalPeriodMonths: 24,
        rationale: 'Required by most state licensure rules and by every payer contract.',
    },
    {
        key: 'TB_SCREENING',
        name: 'TB test / screening',
        category: 'HEALTH_SCREENING',
        requiredForDisciplines: [],
        blocksScheduling: true,
        renewalPeriodMonths: 12,
        rationale: 'A lapsed screening is a communicable-disease exposure in somebody’s home.',
    },
    {
        key: 'PHYSICAL_EXAM',
        name: 'Physical examination',
        category: 'HEALTH_SCREENING',
        requiredForDisciplines: [],
        blocksScheduling: false,
        renewalPeriodMonths: 12,
        rationale: 'Employment record rather than a condition of practice. Warns.',
    },
    {
        key: 'BACKGROUND_CHECK',
        name: 'Criminal background check',
        category: 'BACKGROUND_CHECK',
        requiredForDisciplines: [],
        blocksScheduling: true,
        renewalPeriodMonths: 24,
        rationale: 'Sending an unscreened person into a vulnerable adult’s home is the risk this product exists to manage.',
    },
    {
        key: 'STATE_REGISTRY_CHECK',
        name: 'State registry check',
        category: 'BACKGROUND_CHECK',
        requiredForDisciplines: [],
        blocksScheduling: true,
        renewalPeriodMonths: 12,
        rationale: 'An abuse-registry listing disqualifies outright in every state that keeps one.',
    },
    {
        key: 'DRIVERS_LICENSE',
        name: 'Driver’s licence',
        category: 'LICENSE',
        requiredForDisciplines: [],
        blocksScheduling: false,
        renewalPeriodMonths: null,
        rationale: 'Only matters for a caregiver who transports a client. Warns by default; an agency whose visits all involve driving turns it on.',
    },
    {
        key: 'AUTO_INSURANCE',
        name: 'Auto insurance',
        category: 'DOCUMENT',
        requiredForDisciplines: [],
        blocksScheduling: false,
        renewalPeriodMonths: 12,
        rationale: 'Same as the licence: conditional on transporting, so it warns rather than blocks.',
    },
    {
        key: 'I9',
        name: 'I-9 employment eligibility',
        category: 'DOCUMENT',
        requiredForDisciplines: [],
        blocksScheduling: true,
        renewalPeriodMonths: null,
        rationale: 'Working without one is unlawful employment, not a compliance nicety.',
    },
    {
        key: 'IN_SERVICE_HOURS',
        name: 'Annual in-service hours',
        category: 'TRAINING',
        requiredForDisciplines: [],
        blocksScheduling: false,
        renewalPeriodMonths: 12,
        rationale: 'Blocking every caregiver who is an hour short in December is how an agency stops using the feature. Warns loudly instead.',
    },
    {
        key: 'IMMUNISATION_ATTESTATION',
        name: 'Immunisation attestation',
        category: 'HEALTH_SCREENING',
        requiredForDisciplines: [],
        blocksScheduling: false,
        renewalPeriodMonths: 12,
        rationale: 'Requirements move with public-health policy and with the payer. Warns, and an agency under a mandate switches it on.',
    },
];
/**
 * §17.3's checklist, in the order HR works through it.
 *
 * Not every item blocks. The welcome pack does not stop somebody working; the
 * competency test and the shadow shift do, because they are what the agency
 * relies on to say this person can be sent out alone.
 */
export const SEEDED_ONBOARDING_ITEMS = [
    {
        key: 'DOCUMENTS',
        label: 'Documents collected',
        type: 'DOCUMENT',
        blocksScheduling: true,
        sortOrder: 10,
    },
    {
        key: 'ESIGN_PACKET',
        label: 'Employment packet signed',
        type: 'E_SIGNATURE',
        blocksScheduling: true,
        sortOrder: 20,
    },
    {
        key: 'ORIENTATION',
        label: 'Orientation completed',
        type: 'ORIENTATION',
        blocksScheduling: true,
        sortOrder: 30,
    },
    {
        key: 'COMPETENCY_TEST',
        label: 'Competency test passed',
        type: 'COMPETENCY_TEST',
        blocksScheduling: true,
        sortOrder: 40,
    },
    {
        key: 'SHADOW_SHIFT',
        label: 'Shadow shift completed',
        type: 'SHADOW_SHIFT',
        blocksScheduling: true,
        sortOrder: 50,
    },
];
//# sourceMappingURL=workforce.js.map